import { tap, map, filter, ignoreElements, merge, publish, refCount } from "rxjs/operators";
import { Injectable } from "@angular/core";
import { Observable, ReplaySubject, Subscription } from "rxjs";
import { io } from "socket.io-client";
import { Helper, parseCollection, parseObject } from "@helpers/functions";
import { debug } from "@helpers/debug";
import { HasSocketEvent, Model } from "@models";
import { config } from "@config";

export interface SocketRoom {
	name: string;
	// TODO: eslint fix
	/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
	type: any;
	isPrivate: boolean;
}

export interface SocketEvent {
	event: string;
	args: { rooms: string[]; token?: string };
}

@Injectable({
	providedIn: "root"
})
export class SocketService {
	// TODO: eslint fix
	/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
	private dataObservable: Observable<any>;
	private connectionSubject: ReplaySubject<SocketEvent>;

	private RECONNECTION_ATTEMPTS: number = 15;
	private RECONNECTION_DELAY: number = 20000;
	private TIMEOUT: number = 20000;

	/**
	 * Joins a Room to listen for Events
	 * avalable rooms:
	 * 'users' => UserResponse::class,
	 * 'contacts' => ContactResponse::class,
	 * 'wallets' => WalletResponse::class,
	 * 'wallet_transactions' => WalletTransactionResponse::class,
	 * 'fiat_wallets' => FiatWalletResponse::class,
	 * 'trades' => TradeResponse::class,
	 * 'payout_accounts' => PayoutAccountResponse::class,
	 * 'fiat_wallet_transactions' => FiatWalletTransactionResponse::class,
	 * 'price_alerts' => PriceAlertResponse::class,
	 * 'notifications' => NotificationResponse::class
	 */

	// All rooms
	private currentRooms: string[] = [];

	// Private
	private readonly privateRooms: string[] = [
		"contacts",
		"fiat_wallets",
		"fiat_wallet_transactions",
		"notifications",
		"trades",
		"price_alerts",
		"wallets",
		"wallet_transactions",
		"savings_plan",
		"savings_plans",
		"commodity_wallets",
		"onfido",
		"users",
		"stock_wallets",
		"etf_wallets"
	];

	// Public
	private readonly publicRooms: string[] = [];

	public constructor() {
		this.currentRooms = [...this.privateRooms, ...this.publicRooms];
		this.connectionSubject = this.connect();

		// Private rooms
		this.connectionSubject.next({
			event: "join_auth_rooms",
			args: {
				rooms: this.privateRooms,
				token: Helper.getCookie("access-token")
			}
		});

		// Public rooms
		this.connectionSubject.next({
			event: "join_pub_rooms",
			args: {
				rooms: this.publicRooms
			}
		});
	}

	public joinRoom<T extends Model & HasSocketEvent>(room: SocketRoom): Observable<T> {
		return this.addRoom(room).pipe(map((msg) => parseObject(msg, room.type)));
	}

	public joinCollectionRoom<T extends Model & HasSocketEvent>(room: SocketRoom, collection: string): Observable<T[]> {
		return this.addRoom(room).pipe(
			map((msg) => {
				try {
					return parseCollection<T>(msg, room.type, collection, true);
				} catch (e) {
					console.error("[PARSING] collection: ", e);
					return null;
				}
			}),
			filter((a) => !!a)
		);
	}

	protected connect(): ReplaySubject<SocketEvent> {
		const connectionSubject = new ReplaySubject<SocketEvent>();
		const connection = io(config.socketUrl, {
			reconnection: true,
			reconnectionDelay: this.RECONNECTION_DELAY,
			reconnectionAttempts: this.RECONNECTION_ATTEMPTS,
			timeout: this.TIMEOUT,
			transports: ["websocket", "polling"]
		});

		connection.on("connect", (): void => {
			// Once we are connected emit all the join room events
			const sub: Subscription = connectionSubject.subscribe(
				(value) => {
					connection.emit(value.event, value.args);
					if (debug) {
						console.info("[SOCKET]", "Connected", value);
					}
				},
				(error) => {
					sub?.unsubscribe();
					this.connectionSubject = null;
					this.dataObservable = null;
					this.currentRooms = [];
					if (debug) {
						console.info("[SOCKET]", "Connection ERROR", error);
					}
				},
				() => {
					sub?.unsubscribe();
					this.connectionSubject = null;
					this.dataObservable = null;
					this.currentRooms = [];
					if (debug) {
						console.info("[SOCKET]", "Completed");
					}
				}
			);
		});

		if (debug) {
			// Socket events
			connection.on("connect_error", (x) => {
				console.log("[SOCKET] connect_error", x);
			});
			connection.on("disconnect", (x) => {
				console.log("[SOCKET] disconnect", x);
			});

			// Manager events
			connection.io.on("reconnect_attempt", (x) => {
				if (x <= this.RECONNECTION_ATTEMPTS) {
					console.log("[SOCKET] reconnect_attempt", x);
				}
			});
			connection.io.on("error", (x) => {
				console.log("[SOCKET] error", x);
			});
			connection.io.on("reconnect", (x) => {
				console.log("[SOCKET] reconnect", x);
			});
			connection.io.on("reconnect_error", (x) => {
				console.log("[SOCKET] reconnect_error", x);
			});
			connection.io.on("reconnect_failed", () => {
				console.log("[SOCKET] reconnect_failed");
			});
			connection.io.on("ping", () => {
				console.log("[SOCKET] ping");
			});
		}

		// Send all the created and updated events to the dataObservable
		const dataObs = new Observable((observer) => {
			connection.on("created", (msg: HasSocketEvent) => {
				msg.eventData = "created";
				observer.next(msg);
			});
			connection.on("updated", (msg: HasSocketEvent) => {
				msg.eventData = "updated";
				observer.next(msg);
			});
			connection.on("deleted", (msg: HasSocketEvent) => {
				msg.eventData = "deleted";
				observer.next(msg);
			});
			connection.on("callback_received", (msg: HasSocketEvent) => {
				msg.eventData = "callback_received";
				observer.next(msg);
			});
		});

		this.dataObservable = connectionSubject.pipe(
			ignoreElements(), // Take only Errors from the connection
			merge(dataObs), // Get all the Data Values
			publish(), // Make alle subscribers use the same instance
			refCount()
		); // Manage Connecting and Disconnection automatically
		return connectionSubject;
	}

	private addRoom<T>(room: SocketRoom): Observable<T> {
		if (!this.currentRooms.some((r) => r === room.name)) {
			this.currentRooms.push(room.name);

			if (!this.connectionSubject) {
				this.connectionSubject = this.connect();
			}

			this.connectionSubject.next({
				event: room.isPrivate ? "join_auth_rooms" : "join_pub_rooms",
				args: room.isPrivate
					? {
							rooms: [room.name],
							token: Helper.getCookie("access-token")
						}
					: {
							rooms: [room.name]
						}
			});
		}

		return this.dataObservable.pipe(
			filter((msg) => msg.room === room.name),
			tap(
				(msg) => {
					if (debug && !room.name.includes("prices")) {
						console.info("[SOCKET]", msg);
					}
				},
				(e) => {
					if (debug) {
						console.error("[SOCKET]", e);
					}
				}
			)
		);
	}
}
